import { Component, computed, inject, input } from '@angular/core';
import { ThemeMode, ThemeService } from '../../services/theme.service';
import { BrandLoaderVariant } from '../brand-loader/brand-loader.component';
import {
  LoadingTextComponent,
  LoadingTextSize,
  LoadingTextWord,
  WORD_LABELS,
} from '../loading-text/loading-text.component';

export type LoadingMessageSize = 'mini' | 'medium' | 'large';

const SIZE_PRESETS: Record<LoadingMessageSize, { spinner: number; text: LoadingTextSize; gap: number }> = {
  mini: { spinner: 80, text: 22, gap: 15 },
  medium: { spinner: 120, text: 30, gap: 15 },
  large: { spinner: 160, text: 30, gap: 20 },
};

/**
 * 'Loading Message': the "Cisomate Loader - full cycle" spinner with an animated text graphic below it
 * (or a free-text message). Dark/light artwork follows the active theme.
 * FR/Task ID: Feature 2643 / Task 2862 (spinners & loading icons)
 */
@Component({
  selector: 'app-loading-message',
  standalone: true,
  imports: [LoadingTextComponent],
  templateUrl: './loading-message.component.html',
  styleUrl: './loading-message.component.scss',
  host: {
    role: 'status',
    '[attr.aria-label]': 'accessibleLabel()',
    '[style.gap.px]': 'preset().gap',
  },
})
export class LoadingMessageComponent {
  private readonly themeService = inject(ThemeService);

  /** Size preset from the design: mini (80px), medium (120px), large (160px). */
  readonly size = input<LoadingMessageSize>('mini');

  /** Animated word under the spinner. Ignored when `message` is set. */
  readonly text = input<LoadingTextWord>('loading');

  /** Free text shown under the spinner instead of the animated word. */
  readonly message = input<string>();

  /** Hides the text under the spinner. */
  readonly showText = input(true);

  /** Spinner artwork: the Cisomate Loader, or the Swirl Saver used while saving. */
  readonly spinner = input<BrandLoaderVariant>('loader');

  /** Artwork theme; 'auto' follows the app theme. Sidebars are always light, so they pass 'light'. */
  readonly mode = input<ThemeMode>('auto');

  protected readonly preset = computed(() => SIZE_PRESETS[this.size()]);

  protected readonly spinnerSrc = computed(() => {
    const artwork = this.spinner() === 'swirl' ? 'swirl' : 'cisomate-loader';
    return `assets/loaders/${artwork}-${this.theme()}.svg`;
  });

  /**
   * Accessible name of the status when the visible text is hidden (the spinner image is decorative),
   * so screen readers still announce the loading state. Null when the visible text already names it.
   */
  protected readonly accessibleLabel = computed(() =>
    this.showText() ? null : this.message() || WORD_LABELS[this.text()],
  );

  protected readonly theme = computed(() =>
    this.mode() === 'auto' ? this.themeService.current() : this.mode(),
  );
}
